<script setup>
// Gestão → Monitoramento (qualquer administrador): a operação do período, com
// filtro de caixa e de time. Sem custo e sem nota da equipe (isso é da
// Auditoria). Atualiza a cada minuto. O período, os filtros e o botão do modo
// TV ficam no topo da tela da Gestão (GestaoPainel), que passa os valores.
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { createGestaoApi, duracao, nota } from './api';
import messages from './i18n';

const props = defineProps({
  accountId: { type: [Number, String], required: true },
  dias: { type: Number, default: 0 },
  caixa: { type: String, default: '' },
  time: { type: String, default: '' },
});

// As caixas e os times da empresa, para os filtros do topo.
const emit = defineEmits(['filtros']);

const { t } = useI18n({ messages });
const api = computed(() => createGestaoApi(props.accountId));

const EMOJI = { muito_satisfeito: '😄', satisfeito: '🙂', neutro: '😐', insatisfeito: '😟', irritado: '😡' };
const NIVEIS = Object.keys(EMOJI);
const DIAS_DA_SEMANA = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

const dados = ref(null);
const erro = ref(false);
let relogio = null;

const carrega = async () => {
  try {
    const { data } = await api.value.monitoramento({
      dias: props.dias,
      inbox_id: props.caixa || undefined,
      team_id: props.time || undefined,
    });
    dados.value = data;
    emit('filtros', data.filtros);
    erro.value = false;
  } catch {
    erro.value = true;
  }
};
watch(() => [props.dias, props.caixa, props.time], carrega);
onMounted(() => {
  carrega();
  relogio = setInterval(carrega, 60000);
});
onBeforeUnmount(() => clearInterval(relogio));

const visao = computed(() => dados.value?.visao || {});
const variacao = (atual, antes) => {
  if (!antes) return null;
  const pct = Math.round(((atual - antes) / antes) * 100);
  return {
    texto: t('NEUXS_GESTAO.MONITOR.KPI.VS', {
      sinal: pct >= 0 ? '▲' : '▼',
      n: Math.abs(pct),
      antes: props.dias === 0 ? t('NEUXS_GESTAO.MONITOR.KPI.ONTEM') : t('NEUXS_GESTAO.MONITOR.KPI.ANTES'),
    }),
    cor: pct >= 0 ? 'text-n-teal-11' : 'text-n-ruby-11',
  };
};
const emojiDaMedia = media => {
  if (media === null || media === undefined) return '—';
  const notas = { muito_satisfeito: 5, satisfeito: 4, neutro: 3, insatisfeito: 2, irritado: 1 };
  const nivel = NIVEIS.reduce((melhor, chave) =>
    Math.abs(notas[chave] - media) < Math.abs(notas[melhor] - media) ? chave : melhor
  );
  return EMOJI[nivel];
};

// Gráfico de barras empilhadas (IA embaixo, equipe em cima), numa escala só.
const LARGURA = 720;
const ALTURA = 200;
const M = { esquerda: 36, base: 22, topo: 8 };
const grafico = computed(() => {
  const pontos = dados.value?.por_tempo?.pontos || [];
  const maximo = Math.max(1, ...pontos.map(p => p.ia + p.equipe));
  const util = ALTURA - M.base - M.topo;
  const largura = (LARGURA - M.esquerda) / Math.max(pontos.length, 1);
  const y = v => ALTURA - M.base - (v / maximo) * util;
  const porHora = dados.value?.por_tempo?.unidade === 'hour';
  return {
    barras: pontos.map((p, i) => {
      const x = M.esquerda + i * largura + 2;
      const rotulo = porHora ? `${p.em.slice(11, 13)}h` : p.em.slice(5, 10).split('-').reverse().join('/');
      return {
        x,
        largura: Math.max(largura - 4, 1),
        ia: { y: y(p.ia), altura: ALTURA - M.base - y(p.ia) },
        equipe: { y: y(p.ia + p.equipe), altura: y(p.ia) - y(p.ia + p.equipe) },
        titulo: `${rotulo}: ${p.ia} IA · ${p.equipe} equipe`,
        rotulo: i % Math.max(1, Math.ceil(pontos.length / 8)) === 0 ? rotulo : '',
      };
    }),
    linhas: [0, 0.5, 1].map(f => ({ y: y(maximo * f), valor: Math.round(maximo * f) })),
  };
});

const maiorEscalonamento = computed(() => Math.max(1, ...(dados.value?.ia?.escalonamentos || []).map(e => e.total)));
const funil = computed(() => {
  const estado = dados.value?.disparos?.por_estado || {};
  const entrega = dados.value?.disparos?.entrega || {};
  return [
    { chave: 'ENVIADOS', valor: estado.enviado || 0 },
    { chave: 'ENTREGUES', valor: (entrega.delivered || 0) + (entrega.read || 0) },
    { chave: 'LIDOS', valor: entrega.read || 0 },
    { chave: 'RECUSADOS', valor: entrega.failed || 0, ruim: true },
  ];
});
const maiorFunil = computed(() => Math.max(1, ...funil.value.map(f => f.valor)));
const calor = computed(() => {
  const mapa = {};
  (dados.value?.semana || []).forEach(([dia, hora, n]) => {
    mapa[`${dia}-${hora}`] = n;
  });
  const maximo = Math.max(1, ...Object.values(mapa));
  return { mapa, maximo };
});
const esperando = iso => duracao(Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000)));
const COR_STATUS = { online: 'bg-n-teal-9', busy: 'bg-n-amber-9', offline: 'bg-n-slate-7' };
</script>

<template>
  <div class="flex flex-col gap-5">

    <div v-if="!dados && !erro" class="flex justify-center py-10"><Spinner /></div>
    <div v-else-if="!dados" class="flex items-center gap-3 text-sm text-n-ruby-11">
      {{ t('NEUXS_GESTAO.ERRO') }}
      <Button size="sm" variant="faded" :label="t('NEUXS_GESTAO.TENTAR')" @click="carrega" />
    </div>

    <template v-else>
      <div class="grid grid-cols-2 gap-3 md:grid-cols-4">
        <div class="px-4 py-3 border rounded-xl border-n-weak">
          <span class="text-xs text-n-slate-11">{{ t('NEUXS_GESTAO.MONITOR.KPI.CONVERSAS') }}</span>
          <strong class="block text-heading-1 tabular-nums text-n-slate-12">{{ visao.conversas }}</strong>
          <span v-if="variacao(visao.conversas, visao.conversas_antes)" class="text-xs" :class="variacao(visao.conversas, visao.conversas_antes).cor">
            {{ variacao(visao.conversas, visao.conversas_antes).texto }}
          </span>
        </div>
        <div class="px-4 py-3 border rounded-xl border-n-weak">
          <span class="text-xs text-n-slate-11">{{ t('NEUXS_GESTAO.MONITOR.KPI.AGORA') }}</span>
          <strong class="block text-heading-1 tabular-nums text-n-slate-12">{{ visao.com_a_ia + visao.com_a_equipe }}</strong>
          <span class="text-xs text-n-slate-11">
            {{ t('NEUXS_GESTAO.MONITOR.KPI.AGORA_DICA', { ia: visao.com_a_ia, equipe: visao.com_a_equipe }) }}
          </span>
        </div>
        <div class="px-4 py-3 border rounded-xl" :class="visao.acima_do_sla ? 'border-n-amber-9' : 'border-n-weak'">
          <span class="text-xs text-n-slate-11">{{ t('NEUXS_GESTAO.MONITOR.KPI.FILA') }}</span>
          <strong class="block text-heading-1 tabular-nums text-n-slate-12">{{ visao.na_fila }}</strong>
          <span class="text-xs" :class="visao.acima_do_sla ? 'text-n-ruby-11' : 'text-n-slate-11'">
            {{ t('NEUXS_GESTAO.MONITOR.KPI.FILA_DICA', { n: visao.acima_do_sla, min: dados.sla.minutos }) }}
          </span>
        </div>
        <div class="px-4 py-3 border rounded-xl border-n-weak">
          <span class="text-xs text-n-slate-11">{{ t('NEUXS_GESTAO.MONITOR.KPI.RESOLVIDAS') }}</span>
          <strong class="block text-heading-1 tabular-nums text-n-slate-12">{{ visao.resolvidas }}</strong>
          <span v-if="variacao(visao.resolvidas, visao.resolvidas_antes)" class="text-xs" :class="variacao(visao.resolvidas, visao.resolvidas_antes).cor">
            {{ variacao(visao.resolvidas, visao.resolvidas_antes).texto }}
          </span>
        </div>
        <div class="px-4 py-3 border rounded-xl border-n-weak">
          <span class="text-xs text-n-slate-11">{{ t('NEUXS_GESTAO.MONITOR.KPI.PRIMEIRA') }}</span>
          <strong class="block text-heading-1 tabular-nums text-n-slate-12">{{ duracao(visao.primeira_resposta) }}</strong>
        </div>
        <div class="px-4 py-3 border rounded-xl border-n-weak">
          <span class="text-xs text-n-slate-11">{{ t('NEUXS_GESTAO.MONITOR.KPI.TEMPO') }}</span>
          <strong class="block text-heading-1 tabular-nums text-n-slate-12">{{ duracao(visao.tempo_medio) }}</strong>
        </div>
        <div class="px-4 py-3 border rounded-xl border-n-weak">
          <span class="text-xs text-n-slate-11">{{ t('NEUXS_GESTAO.MONITOR.KPI.CSAT') }}</span>
          <strong class="block text-heading-1 tabular-nums text-n-slate-12">{{ nota(visao.csat) }}</strong>
          <span class="text-xs text-n-slate-11">{{ t('NEUXS_GESTAO.MONITOR.KPI.CSAT_DICA', { n: visao.csat_respostas }) }}</span>
        </div>
        <div class="px-4 py-3 border rounded-xl border-n-weak">
          <span class="text-xs text-n-slate-11">{{ t('NEUXS_GESTAO.MONITOR.KPI.SENTIMENTO') }}</span>
          <strong class="block text-heading-1">{{ emojiDaMedia(dados.sentimento.media) }}</strong>
          <span class="text-xs text-n-slate-11">{{ t('NEUXS_GESTAO.MONITOR.KPI.SENTIMENTO_DICA', { n: dados.sentimento.total }) }}</span>
        </div>
      </div>

      <div class="grid grid-cols-1 gap-5 lg:grid-cols-[2fr_1fr]">
        <section class="flex flex-col gap-2 p-4 border rounded-xl border-n-weak">
          <h2 class="m-0 text-heading-3 text-n-slate-12">
            {{ dados.por_tempo.unidade === 'hour' ? t('NEUXS_GESTAO.MONITOR.POR_HORA') : t('NEUXS_GESTAO.MONITOR.POR_DIA') }}
          </h2>
          <p v-if="!grafico.barras.length" class="m-0 text-sm text-n-slate-11">{{ t('NEUXS_GESTAO.VAZIO') }}</p>
          <svg v-else :viewBox="`0 0 ${LARGURA} ${ALTURA}`" class="w-full h-auto" role="img">
            <g v-for="(linha, i) in grafico.linhas" :key="`l${i}`">
              <line :x1="M.esquerda" :x2="LARGURA" :y1="linha.y" :y2="linha.y" class="stroke-n-weak" stroke-width="1" />
              <text :x="M.esquerda - 6" :y="linha.y + 4" text-anchor="end" class="fill-n-slate-10 text-[10px]">{{ linha.valor }}</text>
            </g>
            <g v-for="(barra, i) in grafico.barras" :key="`b${i}`">
              <title>{{ barra.titulo }}</title>
              <rect :x="barra.x" :y="barra.ia.y" :width="barra.largura" :height="barra.ia.altura" class="fill-n-blue-9" />
              <rect :x="barra.x" :y="barra.equipe.y" :width="barra.largura" :height="barra.equipe.altura" class="fill-n-slate-8" />
              <text v-if="barra.rotulo" :x="barra.x + barra.largura / 2" :y="ALTURA - 6" text-anchor="middle" class="fill-n-slate-10 text-[10px]">
                {{ barra.rotulo }}
              </text>
            </g>
          </svg>
          <div class="flex gap-4 text-xs text-n-slate-11">
            <span><i class="inline-block w-2.5 h-2.5 rounded-sm bg-n-blue-9 me-1.5" />{{ t('NEUXS_GESTAO.MONITOR.SO_IA') }}</span>
            <span><i class="inline-block w-2.5 h-2.5 rounded-sm bg-n-slate-8 me-1.5" />{{ t('NEUXS_GESTAO.MONITOR.COM_EQUIPE') }}</span>
          </div>
        </section>

        <section class="flex flex-col gap-2 p-4 border rounded-xl border-n-weak">
          <h2 class="m-0 text-heading-3 text-n-slate-12">{{ t('NEUXS_GESTAO.MONITOR.SENTIMENTO') }}</h2>
          <p class="m-0 text-xs text-n-slate-11">{{ t('NEUXS_GESTAO.MONITOR.SENTIMENTO_DICA') }}</p>
          <div v-for="nivel in NIVEIS" :key="nivel" class="grid grid-cols-[1.5rem_1fr_2.5rem] items-center gap-2 text-sm">
            <span class="text-base">{{ EMOJI[nivel] }}</span>
            <span class="h-2 overflow-hidden rounded bg-n-alpha-2">
              <span
                class="block h-full bg-n-blue-9"
                :style="{ width: `${dados.sentimento.total ? (100 * (dados.sentimento.niveis[nivel] || 0)) / dados.sentimento.total : 0}%` }"
              />
            </span>
            <span class="text-xs text-end tabular-nums text-n-slate-11">{{ dados.sentimento.niveis[nivel] || 0 }}</span>
          </div>
        </section>
      </div>

      <div class="grid grid-cols-1 gap-5 lg:grid-cols-[1fr_1.6fr]">
        <section class="flex flex-col gap-3 p-4 border rounded-xl border-n-weak">
          <h2 class="m-0 text-heading-3 text-n-slate-12">{{ t('NEUXS_GESTAO.MONITOR.IA') }}</h2>
          <div class="grid grid-cols-3 gap-2">
            <div class="px-3 py-2 rounded-lg bg-n-alpha-2">
              <span class="block text-xs text-n-slate-11">{{ t('NEUXS_GESTAO.MONITOR.SEM_HUMANO') }}</span>
              <strong class="text-lg font-medium tabular-nums">{{ dados.ia.sem_humano === null ? '—' : `${dados.ia.sem_humano}%` }}</strong>
            </div>
            <div class="px-3 py-2 rounded-lg bg-n-alpha-2">
              <span class="block text-xs text-n-slate-11">{{ t('NEUXS_GESTAO.MONITOR.RESPOSTA') }}</span>
              <strong class="text-lg font-medium tabular-nums">
                {{ dados.ia.resposta_media_ms ? `${(dados.ia.resposta_media_ms / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} s` : '—' }}
              </strong>
            </div>
            <div class="px-3 py-2 rounded-lg bg-n-alpha-2">
              <span class="block text-xs text-n-slate-11">{{ t('NEUXS_GESTAO.MONITOR.ERROS') }}</span>
              <strong class="text-lg font-medium tabular-nums" :class="dados.ia.erros ? 'text-n-ruby-11' : ''">{{ dados.ia.erros }}</strong>
            </div>
          </div>
          <p class="m-0 text-xs uppercase tracking-wide text-n-slate-10">{{ t('NEUXS_GESTAO.MONITOR.ESCALOU') }}</p>
          <p v-if="!dados.ia.escalonamentos.length" class="m-0 text-sm text-n-slate-11">{{ t('NEUXS_GESTAO.VAZIO') }}</p>
          <div v-for="item in dados.ia.escalonamentos" :key="item.motivo" class="grid grid-cols-[minmax(0,9rem)_1fr_2rem] items-center gap-2 text-sm">
            <span class="truncate text-n-slate-12">{{ item.motivo }}</span>
            <span class="h-2 overflow-hidden rounded bg-n-alpha-2">
              <span class="block h-full bg-n-blue-9" :style="{ width: `${(100 * item.total) / maiorEscalonamento}%` }" />
            </span>
            <span class="text-xs text-end tabular-nums text-n-slate-11">{{ item.total }}</span>
          </div>
        </section>

        <section class="flex flex-col gap-2 p-4 border rounded-xl border-n-weak">
          <h2 class="m-0 text-heading-3 text-n-slate-12">{{ t('NEUXS_GESTAO.MONITOR.EQUIPE') }}</h2>
          <div class="overflow-x-auto">
            <table class="w-full text-sm">
              <thead>
                <tr class="text-xs text-left text-n-slate-11">
                  <th class="py-2 pe-3 font-normal">{{ t('NEUXS_GESTAO.MONITOR.ATENDENTE') }}</th>
                  <th class="py-2 pe-3 font-normal text-end">{{ t('NEUXS_GESTAO.MONITOR.AGORA') }}</th>
                  <th class="py-2 pe-3 font-normal text-end">{{ t('NEUXS_GESTAO.MONITOR.RESOLVIDAS') }}</th>
                  <th class="py-2 pe-3 font-normal text-end">{{ t('NEUXS_GESTAO.MONITOR.PRIMEIRA') }}</th>
                  <th class="py-2 pe-3 font-normal text-end">{{ t('NEUXS_GESTAO.MONITOR.TEMPO') }}</th>
                  <th class="py-2 font-normal text-end">{{ t('NEUXS_GESTAO.MONITOR.KPI.CSAT') }}</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-n-weak">
                <tr v-for="linha in dados.equipe" :key="linha.id">
                  <td class="py-2 pe-3 text-n-slate-12 whitespace-nowrap">
                    <span
                      class="inline-block w-2 h-2 rounded-full me-2"
                      :class="COR_STATUS[linha.status] || COR_STATUS.offline"
                      :title="t(`NEUXS_GESTAO.MONITOR.STATUS.${COR_STATUS[linha.status] ? linha.status : 'offline'}`)"
                    />
                    {{ linha.nome }}
                  </td>
                  <td class="py-2 pe-3 text-end tabular-nums">{{ linha.agora }}</td>
                  <td class="py-2 pe-3 text-end tabular-nums">{{ linha.resolvidas }}</td>
                  <td class="py-2 pe-3 text-end tabular-nums text-n-slate-11">{{ duracao(linha.primeira_resposta) }}</td>
                  <td class="py-2 pe-3 text-end tabular-nums text-n-slate-11">{{ duracao(linha.tempo_medio) }}</td>
                  <td class="py-2 text-end tabular-nums text-n-slate-11">{{ nota(linha.csat) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      </div>

      <div class="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <section class="flex flex-col gap-2 p-4 border rounded-xl border-n-weak">
          <h2 class="m-0 text-heading-3 text-n-slate-12">{{ t('NEUXS_GESTAO.MONITOR.DISPAROS') }}</h2>
          <div v-for="item in funil" :key="item.chave" class="grid grid-cols-[minmax(0,10rem)_1fr_3.5rem] items-center gap-2 text-sm">
            <span class="text-n-slate-12">{{ t(`NEUXS_GESTAO.MONITOR.${item.chave}`) }}</span>
            <span class="h-5 overflow-hidden rounded bg-n-alpha-2">
              <span class="block h-full" :class="item.ruim ? 'bg-n-ruby-9 opacity-70' : 'bg-n-blue-9'" :style="{ width: `${(100 * item.valor) / maiorFunil}%` }" />
            </span>
            <span class="text-end tabular-nums text-n-slate-12">{{ item.valor.toLocaleString('pt-BR') }}</span>
          </div>
        </section>

        <section class="flex flex-col gap-3 p-4 border rounded-xl border-n-weak">
          <h2 class="m-0 text-heading-3 text-n-slate-12">{{ t('NEUXS_GESTAO.MONITOR.ASSUNTOS') }}</h2>
          <div class="flex flex-wrap gap-2">
            <span v-if="!Object.keys(dados.assuntos).length" class="text-sm text-n-slate-11">{{ t('NEUXS_GESTAO.VAZIO') }}</span>
            <span v-for="(n, etiqueta) in dados.assuntos" :key="etiqueta" class="px-2.5 py-1 text-xs border rounded-full border-n-weak bg-n-alpha-1">
              {{ etiqueta }} <strong class="ms-1 font-medium text-n-blue-11 tabular-nums">{{ n }}</strong>
            </span>
          </div>
          <p class="m-0 mt-2 text-xs uppercase tracking-wide text-n-slate-10">{{ t('NEUXS_GESTAO.MONITOR.SLA', { min: dados.sla.minutos }) }}</p>
          <p v-if="!dados.sla.estourados.length" class="m-0 text-sm text-n-slate-11">{{ t('NEUXS_GESTAO.MONITOR.SLA_VAZIO') }}</p>
          <div
            v-for="item in dados.sla.estourados"
            :key="item.conversa"
            class="flex items-center gap-3 px-3 py-2 text-sm rounded-lg bg-n-alpha-1"
          >
            <span class="w-2 h-2 rounded-full bg-n-ruby-9" />
            <span class="flex-1 truncate text-n-slate-12">{{ item.cliente }}<template v-if="item.time"> · {{ item.time }}</template></span>
            <span class="tabular-nums text-n-ruby-11">{{ esperando(item.esperando_desde) }}</span>
          </div>
        </section>
      </div>

      <section class="flex flex-col gap-2 p-4 border rounded-xl border-n-weak">
        <h2 class="m-0 text-heading-3 text-n-slate-12">{{ t('NEUXS_GESTAO.MONITOR.SEMANA') }}</h2>
        <p class="m-0 text-xs text-n-slate-11">{{ t('NEUXS_GESTAO.MONITOR.SEMANA_DICA') }}</p>
        <div class="overflow-x-auto">
          <div class="grid gap-0.5 text-[10px] text-n-slate-10 min-w-[36rem]" style="grid-template-columns: 2.5rem repeat(24, minmax(0, 1fr))">
            <span />
            <span v-for="h in 24" :key="`h${h}`" class="text-center">{{ (h - 1) % 6 === 0 ? h - 1 : '' }}</span>
            <template v-for="(nome, dia) in DIAS_DA_SEMANA" :key="`d${dia}`">
              <span>{{ nome }}</span>
              <span
                v-for="h in 24"
                :key="`c${dia}-${h}`"
                class="rounded-sm aspect-square bg-n-blue-9"
                :style="{ opacity: 0.06 + (0.94 * (calor.mapa[`${dia}-${h - 1}`] || 0)) / calor.maximo }"
                :title="`${nome} ${h - 1}h: ${calor.mapa[`${dia}-${h - 1}`] || 0}`"
              />
            </template>
          </div>
        </div>
      </section>
    </template>
  </div>
</template>
